import _         from 'lodash';
import Immutable from 'immutable';

import { formatDate, parseDate } from 'alto-utils/lib/utils';
import CanteenRepository          from 'alto-react-canteen/lib/models';

import Canteen     from './canteen';
import Course      from './course';
import CourseGroup from './course_group';
import CanteenMenu from './canteen_menu';
import DailyMenu   from './daily_menu';
import GroupMenu   from './group_menu';
import CourseMenu  from './course_menu';

export default class Menu {
  static initClass() {
    this.prototype.canteenMenuClass = CanteenMenu;
    this.prototype.dailyMenuClass   = DailyMenu;
    this.prototype.groupMenuClass   = GroupMenu;
  }

  constructor(_canteenMenus, canteens, dates, courses, groups, settings) {
    this._canteenMenus = _canteenMenus;
    this.canteens      = canteens;
    this.dates         = dates;
    this.courses       = courses;
    this.groups        = groups;
    this.settings      = settings;
  }

  with(canteenMenus) {
    return new this.constructor(
      canteenMenus, this.canteens, this.dates, this.courses, this.groups, this.settings
    );
  }

  static load(options = {}) {
    return this.loadMeals(options).then(meals => this.fromMeals(meals, options));
  }

  static loadMeals(options = {}) {
    const endDate = options.startDate.clone().add(options.numberOfDays - 1, 'days');

    return this._loadMeals({ ...options, endDate });
  }

  static fromMeals(meals, { settings, startDate, numberOfDays }) {
    const dates = Immutable.List(
      _.range(numberOfDays).map(i => startDate.clone().add(i, 'days'))
    );

    const canteens = meals.secondaryFor(Canteen);
    const courses  = meals.secondaryFor(Course);
    const groups   = meals.secondaryFor(CourseGroup);

    const canteenMenus = this._canteenMenusFor(meals, canteens, courses, groups);

    return new (this)(canteenMenus, canteens, dates, courses, groups, settings);
  }

  static _loadMeals({ backend, startDate, endDate }) {
    const repository = CanteenRepository.for(backend.baseUrl);
    const relation   = repository.meals.with('canteen', 'course', 'course.group');

    return backend.request('GET', '/canteen/load_menu',
      { qs: { from_date: formatDate(startDate), to_date: formatDate(endDate) } }
    ).then(response => relation.deserializeMany(response));
  }

  static _canteenMenusFor(meals, canteens, courses, groups) {
    return meals.groupBy('canteenID').map((mealsForCanteen, canteenID) => {
      const canteen    = canteens.find(canteenID);
      const dailyMenus = mealsForCanteen
        .groupBy(meal => formatDate(meal.date))
        .map((mealsForDate, formattedDate) => {
          const groupMenus = mealsForDate
            .groupBy('associations.course.groupName')
            .map((mealsForCourseGroup, _groupName) => {
              let courseMenus = mealsForCourseGroup
                .groupBy('courseName')
                .map((mealsForCourse, courseName) =>
                  this._courseMenuFor(canteen, parseDate(formattedDate), courses.find(courseName), mealsForCourse.entities())
                );

              courseMenus = this._postprocessCourseMenus(courseMenus, courses);

              return courseMenus.sortBy((_cm, courseName) => courses.find(courseName).orderNumber);
            });

          return groupMenus.sortBy((_gm, groupName) => groups.find(groupName).orderNumber);
        });

      return dailyMenus.sortBy((_dm, date) => date);
    });
  }

  static _courseMenuFor(canteen, date, course, meals) {
    return new CourseMenu(canteen, date, course, meals);
  }

  static _postprocessCourseMenus(courseMenus, _courses) {
    return courseMenus;
  }

  forCanteen(canteenID) {
    const canteen    = this.canteens.safeFind(canteenID);
    const dailyMenus = this._canteenMenus.getIn([canteenID]) || null;

    return new this.canteenMenuClass(canteen, dailyMenus, this);
  }

  forDate(canteenID, date) {
    const canteen    = this.canteens.safeFind(canteenID);
    const groupMenus = this._canteenMenus.getIn([canteenID, formatDate(date)]) || null;

    return new this.dailyMenuClass(canteen, date, groupMenus, this);
  }

  forGroup(canteenID, date, group) {
    return this.safeForGroup(canteenID, date, group) ?? throw new Error(
      `Group menu for canteen ${canteenID}, date ${formatDate(date)} ` +
      `and group ${group.name} not found`
    );
  }

  safeForGroup(canteenID, date, group) {
    const canteen     = this.canteens.safeFind(canteenID);
    const courseMenus = this._canteenMenus.getIn([canteenID, formatDate(date), group.name]);

    return canteen && courseMenus && new this.groupMenuClass(canteen, date, group, courseMenus, this);
  }

  datesWithMeals() {
    return this.dates.filter(date => this.hasMealsForDate(date));
  }

  hasMealsForDate(date) {
    return this._canteenMenus.some(dailyMenus => dailyMenus.get(formatDate(date)));
  }
};

Menu.initClass();
